

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Paper, Avatar,
  Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Alert, Tooltip,
  TextField, ButtonGroup, Badge, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, List,
  ListItem, ListItemIcon, ListItemText, TablePagination, Select, MenuItem, FormControl
} from '@mui/material';
import {
  Biotech, ShoppingCart, HourglassEmpty, 
  CheckCircle, Cancel, Assessment, ArrowForward, 
  Warning, Refresh, CalendarToday, TrendingUp, ErrorOutline,
  Close as CloseIcon, Person as UserIcon, Phone as PhoneIcon,
  Email as EmailIcon, Wc as GenderIcon, DateRange as AgeIcon,
  Payment as PaymentIcon, Description as DescriptionIcon,
  LocalHospital as ActivityIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import CustomDialog from "../../CustomDialog"

const DiagnosticAdminScreens = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [partialDataWarning, setPartialDataWarning] = useState(false);
  
  // Dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // CustomDialog state (replacing Snackbar)
  const [dialog, setDialog] = useState({ 
    open: false, 
    message: '', 
    title: 'Notification',
    type: 'info' 
  });
  
  // Date filter state
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customDateMode, setCustomDateMode] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const defaultDashboardData = {
    diagnosticOrders: {
      total: 0, pending: 0, completed: 0, cancelled: 0,
      report_ready: 0, revenue: 0, completion_rate: 0
    },
    complaints: { total: 0, active: 0, resolved: 0 },
    recentOrders: [],
    activeUsers: 0,
    revenueGrowth: 0,
    usersGrowth: 0,
    metadata: null
  };

  const [dashboardData, setDashboardData] = useState(defaultDashboardData);

  const dashboardApi = `${process.env.REACT_APP_DASHBOARD_ANALYTICS_API}?type=booking`;

  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
    fetchDashboardData();
  }, []);

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      setPartialDataWarning(false);

      let url = dashboardApi;
      const params = new URLSearchParams();
      
      if (customDateMode && startDate && endDate) {
        params.append('start_date', new Date(startDate).toISOString());
        params.append('end_date', new Date(endDate).toISOString());
      } else {
        params.append('period', selectedPeriod);
      }
      
      if (params.toString()) {
        url += `&${params.toString()}`;
      }

      const response = await axios.get(url, {
        timeout: 30000,
        validateStatus: (status) => status < 500,
      });

      if (response.data.success) {
        const filteredData = {
          diagnosticOrders: response.data.diagnosticOrders || defaultDashboardData.diagnosticOrders,
          complaints: response.data.complaints || defaultDashboardData.complaints,
          recentOrders: (response.data.recentOrders || []).filter(order => 
            order.type === 'Diagnostic' || order.serviceType?.toLowerCase().includes('diagnostic')
          ),
          activeUsers: response.data.activeUsers || 0,
          revenueGrowth: response.data.revenueGrowth || 0,
          usersGrowth: response.data.usersGrowth || 0,
          metadata: response.data.metadata
        };
        setDashboardData(filteredData);
        
        // Reset to first page when data changes
        setPage(0);
        
        if (isRefresh) {
          setDialog({
            open: true,
            message: 'Dashboard data has been successfully refreshed with the latest information.',
            title: 'Success',
            type: 'success'
          });
        }
      } else {
        setPartialDataWarning(true);
        const mergedData = {
          ...defaultDashboardData,
          ...response.data,
          recentOrders: (response.data.recentOrders || []).filter(order => 
            order.type === 'Diagnostic' || order.serviceType?.toLowerCase().includes('diagnostic')
          ),
        };
        setDashboardData(mergedData);
        
        setDialog({
          open: true,
          message: 'Dashboard loaded successfully, but some data may be incomplete or unavailable.',
          title: 'Partial Data',
          type: 'warning'
        });
      }
    } catch (err) {
      console.error('Diagnostic dashboard fetch error:', err);
      
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your connection.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to load diagnostic dashboard analytics');
      }
      
      if (!isRefresh) {
        setDashboardData(defaultDashboardData);
      }
      
      setDialog({
        open: true,
        message: 'Unable to fetch the latest dashboard data. Please check your connection and try again.',
        title: 'Error',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dashboardApi, customDateMode, startDate, endDate, selectedPeriod]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setCustomDateMode(false);
    setTimeout(() => fetchDashboardData(), 100);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setDialog({
          open: true,
          message: 'The start date must be earlier than or equal to the end date. Please adjust your selection.',
          title: 'Invalid Date Range',
          type: 'error'
        });
        return;
      }
      setCustomDateMode(true);
      fetchDashboardData();
    } else {
      setDialog({
        open: true,
        message: 'Please select both a start date and an end date to apply the custom filter.',
        title: 'Missing Dates',
        type: 'warning'
      });
    }
  };

  const handleCloseDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open booking details dialog
  const handleOpenBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  // Close booking details dialog
  const handleCloseBookingDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedBooking(null);
  };

  // Navigate to full orders page
  const handleViewAllOrders = () => {
    navigate("/orders-management");
  };

  // Calculate paginated data
  const paginatedOrders = dashboardData.recentOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const StatCard = ({ title, value, icon, color, subtitle, growth }) => (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}20`,
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {growth !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {growth >= 0 ? (
              <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
              <TrendingUp sx={{ fontSize: 18, color: 'error.main', transform: 'rotate(180deg)' }} />
            )}
            <Typography variant="caption" fontWeight="600" color={growth >= 0 ? 'success.main' : 'error.main'}>
              {Math.abs(growth).toFixed(1)}% vs last period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const OrderStatusCard = ({ title, value, icon, color, percentage }) => (
    <Card sx={{ 
      p: 2.5, 
      height: '100%',
      border: `1px solid ${color}20`,
      bgcolor: `${color}05`,
      transition: 'all 0.3s ease',
      '&:hover': { boxShadow: `0 4px 12px ${color}20` }
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color={color}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>
          {icon}
        </Avatar>
      </Stack>
      {percentage !== undefined && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {percentage}% of total
          </Typography>
        </Box>
      )}
    </Card>
  );

  const getStatusChip = (status) => {
    const configs = {
      'new': { label: 'New', color: 'info', icon: <HourglassEmpty fontSize="small" /> },
      'Confirmed': { label: 'Confirmed', color: 'primary', icon: <CheckCircle fontSize="small" /> },
      'sample-collected': { label: 'Sample Collected', color: 'info', icon: <Biotech fontSize="small" /> },
      'test-in-progress': { label: 'Testing', color: 'warning', icon: <Assessment fontSize="small" /> },
      'report-ready': { label: 'Report Ready', color: 'secondary', icon: <CheckCircle fontSize="small" /> },
      'report-delivered': { label: 'Delivered', color: 'success', icon: <CheckCircle fontSize="small" /> },
      'closed': { label: 'Closed', color: 'default', icon: <CheckCircle fontSize="small" /> },
      'cancelled': { label: 'Cancelled', color: 'error', icon: <Cancel fontSize="small" /> },
    };
    const config = configs[status] || { label: status, color: 'default', icon: null };
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small"
        icon={config.icon}
        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !refreshing) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        bgcolor: 'grey.50' 
      }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#006D77', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading Diagnostic Dashboard...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'grey.50', 
      pb: 4,
      ml: { xs: 0, md: '280px' },
      mt: { xs: '56px', md: 0 }, 
    }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h3" fontWeight={700} color="#006D77" gutterBottom>
                Diagnostic Admin Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                {dashboardData.metadata?.displayRange || 'Today'} • Monitor diagnostic bookings & operations
              </Typography>
              {partialDataWarning && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <ErrorOutline sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="caption" color="warning.main">
                    Some data may be incomplete
                  </Typography>
                </Box>
              )}
            </Box>
            <Stack direction="row" spacing={3} alignItems="center">
              <Badge 
                badgeContent={dashboardData.complaints?.active || 0} 
                color="error"
                sx={{ '& .MuiBadge-badge': { right: -10, top: 10 } }}
              >
                <Button 
                  variant="outlined" 
                  startIcon={<Warning />}
                  onClick={() => navigate('/complaints-dashboard')}
                  sx={{ 
                    borderColor: '#006D77',
                    color: '#006D77',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#006D77', bgcolor: '#006D7715' },
                  }}
                >
                  Active Complaints
                </Button>
              </Badge>
              <Tooltip title="Refresh dashboard">
                <IconButton
                  onClick={() => fetchDashboardData(true)}
                  disabled={refreshing}
                  sx={{
                    bgcolor: '#006D77',
                    color: 'white',
                    width: 48,
                    height: 48,
                    '&:hover': { bgcolor: '#005761' },
                    '&:disabled': { bgcolor: '#ccc' },
                    ...(refreshing && { '& svg': { animation: 'spin 1s linear infinite' } })
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => setError(null)}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => fetchDashboardData(true)}
              >
                RETRY
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Date Filter */}
        <Paper elevation={1} sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Avatar sx={{ bgcolor: '#006D7715', color: '#006D77' }}>
                <CalendarToday />
              </Avatar>
              <Typography variant="h5" fontWeight={600}>
                Filter by Date
              </Typography>
            </Stack>

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <ButtonGroup variant="outlined" fullWidth sx={{ height: 48 }}>
                  {['today', 'week', 'month', 'all'].map(period => (
                    <Button
                      key={period}
                      onClick={() => handlePeriodChange(period)}
                      variant={selectedPeriod === period && !customDateMode ? 'contained' : 'outlined'}
                      sx={{
                        flex: 1,
                        fontWeight: 600,
                        textTransform: 'none',
                        ...(selectedPeriod === period && !customDateMode && {
                          bgcolor: '#006D77',
                          color: 'white',
                          '&:hover': { bgcolor: '#005761' }
                        })
                      }}
                    >
                      {period === 'today' ? 'Today' : 
                       period === 'week' ? '7 Days' : 
                       period === 'month' ? 'This Month' : 'All Time'}
                    </Button>
                  ))}
                </ButtonGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCustomDateApply}
                    disabled={!startDate || !endDate}
                    sx={{ 
                      minWidth: 100, 
                      bgcolor: '#006D77', 
                      '&:hover': { bgcolor: '#005761' },
                      '&:disabled': { bgcolor: '#ccc' }
                    }}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {customDateMode && (
              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                Showing diagnostic data from {startDate} to {endDate}
              </Alert>
            )}
          </CardContent>
        </Paper>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Bookings"
              value={dashboardData.diagnosticOrders.total}
              icon={<ShoppingCart />}
              color="#006D77"
              subtitle="In selected period"
              growth={dashboardData.revenueGrowth}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Complaints"
              value={dashboardData.complaints?.active || 0}
              icon={<Warning />}
              color="#F77F00"
              subtitle="Require attention"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Completion Rate"
              value={`${dashboardData.diagnosticOrders.completion_rate || 0}%`}
              icon={<CheckCircle />}
              color="#52B788"
              subtitle="Successfully completed"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value={`₹${((dashboardData.diagnosticOrders.revenue || 0) / 1000).toFixed(1)}K`}
              icon={<Biotech />}
              color="#2E86AB"
              subtitle="In selected period"
            />
          </Grid>
        </Grid>

        {/* Order Status Distribution */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 6.5, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: '#006D77' }}>
                Booking Status Distribution
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <OrderStatusCard
                    title="Pending"
                    value={dashboardData.diagnosticOrders.pending}
                    icon={<HourglassEmpty />}
                    color="#F77F00"
                    percentage={Math.round((dashboardData.diagnosticOrders.pending / (dashboardData.diagnosticOrders.total || 1)) * 100)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <OrderStatusCard
                    title="Report Ready"
                    value={dashboardData.diagnosticOrders.report_ready}
                    icon={<Assessment />}
                    color="#2E86AB"
                    percentage={Math.round((dashboardData.diagnosticOrders.report_ready / (dashboardData.diagnosticOrders.total || 1)) * 100)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <OrderStatusCard
                    title="Completed"
                    value={dashboardData.diagnosticOrders.completed}
                    icon={<CheckCircle />}
                    color="#52B788"
                    percentage={Math.round((dashboardData.diagnosticOrders.completed / (dashboardData.diagnosticOrders.total || 1)) * 100)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <OrderStatusCard
                    title="Cancelled"
                    value={dashboardData.diagnosticOrders.cancelled}
                    icon={<Cancel />}
                    color="#EF476F"
                    percentage={Math.round((dashboardData.diagnosticOrders.cancelled / (dashboardData.diagnosticOrders.total || 1)) * 100)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Diagnostic Bookings Table */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
              <Box>
                <Typography variant="h5" fontWeight={700} color="#006D77">
                  Recent Diagnostic Bookings
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Latest activity • Only showing diagnostic bookings
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    displayEmpty
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#006D77'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#005761'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#006D77'
                      }
                    }}
                  >
                    <MenuItem value={5}>Show 5</MenuItem>
                    <MenuItem value={10}>Show 10</MenuItem>
                    <MenuItem value={20}>Show 20</MenuItem>
                    <MenuItem value={50}>Show 50</MenuItem>
                  </Select>
                </FormControl>
                <Button 
                  variant="contained" 
                  endIcon={<ArrowForward />}
                  onClick={handleViewAllOrders}
                  sx={{ 
                    bgcolor: '#006D77',
                    textTransform: "none", 
                    '&:hover': { bgcolor: '#005761' } 
                  }}
                >
                  View All Bookings
                </Button>
              </Stack>
            </Stack>

            {dashboardData.recentOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <Assessment sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No diagnostic bookings found
                </Typography>
                <Typography variant="body2">
                  {customDateMode ? `Try adjusting the date range from ${startDate} to ${endDate}` : 'Select a different time period'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#006D7710' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#006D77' }}>Booking ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#006D77' }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#006D77' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#006D77' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#006D77' }}>Date</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#006D77' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedOrders.map((order) => (
                        <TableRow 
                          key={order.id}
                          hover
                          onClick={(e) => {
                            // Prevent row click when clicking on action button
                            if (!e.target.closest('.action-button')) {
                              handleOpenBookingDetails(order);
                            }
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#006D7705' }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="#006D77">
                              {order.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {order.customer}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              ₹{order.amount?.toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(order.status)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(order.date).toLocaleDateString('en-IN', { 
                                day: '2-digit', month: 'short', year: 'numeric' 
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              className="action-button"
                              size="small" 
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenBookingDetails(order);
                              }}
                            >
                              <ArrowForward fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>


                  <Typography variant="body2" color="text.secondary">
                    Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, dashboardData.recentOrders.length)} of {dashboardData.recentOrders.length} bookings
                  </Typography>
                  <TablePagination
                    component="div"
                    count={dashboardData.recentOrders.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    sx={{
                      '& .MuiTablePagination-select': {
                        display: 'none'
                      },
                      '& .MuiTablePagination-selectLabel': {
                        display: 'none'
                      },
                      '& .MuiTablePagination-toolbar': {
                        minHeight: 'auto',
                        paddingLeft: 0
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Paper>
      </Container>

      {/* Booking Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleCloseBookingDetails} 
        maxWidth="md" 
        fullWidth
        scroll="paper"
      >
        {selectedBooking && (
          <>
            <DialogTitle sx={{ 
              bgcolor: '#006D77', 
              color: 'white', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Booking Details
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  ID: {selectedBooking.id}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseBookingDetails} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Status Badge */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {getStatusChip(selectedBooking.status)}
                  <Chip 
                    label={`₹${selectedBooking.amount?.toLocaleString('en-IN')}`} 
                    color="success"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                <Divider />

                {/* Customer Information */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#006D77', mb: 2 }}>
                    Customer Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <EmailIcon sx={{ color: '#006D77' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={selectedBooking.email}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PhoneIcon sx={{ color: '#006D77' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Phone" 
                        secondary={selectedBooking.phone || 'N/A'}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </Paper>

                {/* Booking Details */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#006D77', mb: 2 }}>
                    Booking Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Service Type</Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {selectedBooking.type || 'Diagnostic'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Booking Date</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatDate(selectedBooking.date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {getStatusChip(selectedBooking.status)}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="h6" color="success.main" fontWeight="bold">
                        ₹{selectedBooking.amount?.toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Additional Information */}
                {selectedBooking.notes && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#006D77', mb: 2 }}>
                      <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Additional Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.notes}
                    </Typography>
                  </Paper>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCloseBookingDetails}
                    sx={{
                      borderColor: '#006D77',
                      color: '#006D77',
                      '&:hover': {
                        borderColor: '#005761',
                        bgcolor: 'rgba(0, 109, 119, 0.08)'
                      },
                      px: 4
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* CustomDialog replacing Snackbar */}
      <CustomDialog
        open={dialog.open}
        message={dialog.message}
        title={dialog.title}
        type={dialog.type}
        onClose={handleCloseDialog}
        buttonText="OK"
      />
    </Box>
  );
};

export default DiagnosticAdminScreens;